<script>
import { mapState } from 'vuex';
import formatISO from 'date-fns/formatISO';
import { preferredName } from '@/lib/agreement/filters';
import holidayScheduleJurisdiction from '@/lib/holidayScheduleProvince';
import { auth } from '@/firebase';
import { trialActiveForClients, hasEditPermission } from '@/mixins/roleLevelPermissions';
import ParentingHub from '@/components/ParentingSchedule/parenting/ParentingHub.vue';
import toast from '@/mixins/toast';
import { onError as onRotationError } from '@/components/ParentingSchedule/parenting/rotationDraft.js';
import { onError as onHolidayError } from '@/components/ParentingSchedule/holiday/holidayDraft.js';

// Saves are debounced and fire in the background, so nothing in the builders
// awaits them and a rejected write would otherwise only reach the console. The
// edit stays on screen either way, which is what makes silence dangerous: the
// user has no reason to doubt it saved until they reopen the agreement.
const SAVE_FAILED = 'Your last change could not be saved. Check your connection and try again — the change is still on screen, but it will be lost if you leave this page.';
const CLEAR_FAILED = 'The schedule could not be reset. Reload the page to see what is actually saved.';

const PRONOUN_MAP = {
	male: 'he/him',
	female: 'she/her',
	neutral: 'they/them',
};

// Birthdates are stored as ISO timestamps ("2014-02-02T08:00:00.000Z") but
// schedule-core's age-of-majority filter wants a plain calendar date — and the
// LOCAL one: the builder saves a locally-parsed date via toISOString(), so
// slicing the UTC string shifts the day either side of UTC. formatISO is
// local-time by default, which is exactly that.
function toIsoDate(value) {
	if (!value) return null;
	if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return String(value);
	const d = new Date(value);
	return isNaN(d.getTime()) ? null : formatISO(d, { representation: 'date' });
}

// Page box for our prints, injected as the LAST stylesheet in <head> while a
// builder's print runs (they add the body class right before window.print())
// and removed on afterprint. @page cannot be gated by a body class, and every
// tab on the agreement page declares its own unscoped @page, so which one
// wins otherwise depends on which chunk loaded last. ParentalPlanner's rule
// puts "Page N of M" in @bottom-right, and Chrome 131+ honours margin boxes,
// so it showed up under the Divii logo on our PDFs. Injecting last makes the
// letter box and the empty margin boxes win every time, and removing it on
// afterprint means our page box no longer leaks into other tabs' prints.
const PRINT_PAGE_STYLE_ID = 'parenting-schedule-print-page';
const PRINT_PAGE_CSS = `@page {
	size: letter portrait;
	margin: 0.4in 0.5in;
	@top-left { content: none; }
	@top-center { content: none; }
	@top-right { content: none; }
	@bottom-left { content: none; }
	@bottom-center { content: none; }
	@bottom-right { content: none; }
}`;

export default {
	name: 'ParentingSchedulePanel',
	components: { ParentingHub },
	mixins: [toast],
	props: {
		agreementData: {
			type: Object,
			default: null,
		},
	},
	created() {
		onRotationError(this.reportScheduleError);
		onHolidayError(this.reportScheduleError);
	},
	mounted() {
		window.addEventListener('beforeprint', this.installPrintPage);
		window.addEventListener('afterprint', this.removePrintPage);
	},
	beforeDestroy() {
		window.removeEventListener('beforeprint', this.installPrintPage);
		window.removeEventListener('afterprint', this.removePrintPage);
		this.removePrintPage();
	},
	computed: {
		...mapState(['user', 'permissions']),
		sourceAgreement() {
			return this.agreementData || null;
		},
		partyAName() {
			return preferredName(this.sourceAgreement?.partner1) || 'Party A';
		},
		partyBName() {
			return preferredName(this.sourceAgreement?.partner2) || 'Party B';
		},
		partyAPronouns() {
			return PRONOUN_MAP[this.sourceAgreement?.partner1?.pronouns] || '';
		},
		partyBPronouns() {
			return PRONOUN_MAP[this.sourceAgreement?.partner2?.pronouns] || '';
		},
		jurisdiction() {
			return holidayScheduleJurisdiction(this.sourceAgreement);
		},
		caseId() {
			return this.$route?.params?.id || this.sourceAgreement?.id || null;
		},
		// Self-supporting children are outside the parenting schedule, same as the
		// Parenting tab's own visibility rule in Agreement.vue. dob lets
		// schedule-core drop children who have reached the age of majority from
		// the generated wording.
		children() {
			return (this.sourceAgreement?.children || [])
				.filter((child) => !child?.selfSupporting)
				.map((child) => {
					const name = preferredName(child);
					return name ? { name, dob: toIsoDate(child.birthdate) } : null;
				})
				.filter(Boolean);
		},
		// Mirrors firestore.rules for this agreement's subcollections: only
		// primaryLawyer / primarySupport / clientUser may write. Secondary lawyers
		// and support staff get the builders in read-only mode instead of edits
		// that fail silently at the server. Fails open — an unrecognised user
		// still hits the server rule.
		role() {
			if (this.isTrialReadOnly || this.isViewOnly) return 'viewer';
			const uid = this.user?.uid || auth.currentUser?.uid;
			const mine = uid ? this.permissions?.[uid] : null;
			if (!mine) return 'primary';
			return mine.secondaryLawyer === true || mine.secondarySupport === true
				? 'secondary'
				: 'primary';
		},
		isTrialReadOnly() {
			return !!trialActiveForClients();
		},
		isViewOnly() {
			return !hasEditPermission('parenting-schedule');
		},
		currentUserName() {
			return this.user?.displayName || this.user?.email || '';
		},
	},
	methods: {
		// See PRINT_PAGE_CSS. Only acts on prints started by our builders.
		installPrintPage() {
			if (!document.body.classList.contains('parenting-schedule-printing')) return;
			this.removePrintPage();
			const el = document.createElement('style');
			el.id = PRINT_PAGE_STYLE_ID;
			el.textContent = PRINT_PAGE_CSS;
			document.head.appendChild(el);
		},
		removePrintPage() {
			const el = document.getElementById(PRINT_PAGE_STYLE_ID);
			if (el) el.remove();
		},
		reportScheduleError(action) {
			const message = action === 'clear' ? CLEAR_FAILED : SAVE_FAILED;
			this.errorMessage('Parenting schedule', message);
		},
	},
};
</script>

<template>
	<div v-if="caseId" class="parenting-schedule-panel" :class="{ 'trial-read-only': isTrialReadOnly }">
		<b-alert v-if="isTrialReadOnly" show variant="info" class="print-hide">
			You are viewing the parenting schedule during your free trial. Choose a plan to build and edit it.
		</b-alert>
		<b-alert v-else-if="isViewOnly" show variant="primary" class="print-hide">
			<strong>View only.</strong> You can see the parenting schedule but can't make changes.
		</b-alert>
		<ParentingHub
			:party-a-name="partyAName"
			:party-b-name="partyBName"
			:party-a-pronouns="partyAPronouns"
			:party-b-pronouns="partyBPronouns"
			:jurisdiction="jurisdiction"
			:case-id="caseId"
			:children="children"
			:current-user-name="currentUserName"
			:role="role"
		/>
	</div>
</template>

<style scoped>
/* Same shape as PropertySchedule: the panel itself has no fixed height, so it
   can never leave dead space below its content. Only the inner scroll region is
   capped (see ParentingHub .hub-panel). `height: calc(100vh - 180px)`, copied
   from the old Holiday Schedule panel, is what produced the white gap — the
   panel starts ~118px down, so 180 always left ~60px unused. */
.parenting-schedule-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
}
</style>

<style>

.parenting-schedule-panel.trial-read-only .row-flag,
.parenting-schedule-panel.trial-read-only .discussion-note-panel,
.parenting-schedule-panel.trial-read-only .viewing-note,
.parenting-schedule-panel.trial-read-only .btn-download {
	display: none !important;
}

/* Print rules below are gated on the body class both builders toggle around
   window.print(), so nothing here affects prints started from another tab.
   @page cannot be gated at all — see the note on that rule. */
@media print {
	body.parenting-schedule-printing .print-hide,
	body.parenting-schedule-printing .agreement-navigation,
	body.parenting-schedule-printing .active-user,
	body.parenting-schedule-printing .nav-bar,
	body.parenting-schedule-printing .left-col,
	body.parenting-schedule-printing #intercom-container,
	body.parenting-schedule-printing .intercom-lightweight-app,
	body.parenting-schedule-printing .intercom-app,
	body.parenting-schedule-printing [class*="intercom"],
	body.parenting-schedule-printing [class*="userflowjs"] {
		display: none !important;
	}
	/* Bootstrap row/col negative margins shift print content under the page edge. */
	body.parenting-schedule-printing .content-row {
		margin: 0 !important;
		height: auto !important;
		flex-wrap: wrap !important;
	}
	body.parenting-schedule-printing .right {
		padding: 0 !important;
		margin: 0 !important;
		flex: 0 0 100% !important;
		max-width: 100% !important;
		width: 100% !important;
	}
	body.parenting-schedule-printing .tab-content,
	body.parenting-schedule-printing .tab-content > div,
	body.parenting-schedule-printing .custom-scroll,
	body.parenting-schedule-printing .parenting-schedule-panel,
	body.parenting-schedule-printing .parenting-hub,
	body.parenting-schedule-printing .parenting-hub .hub-panel,
	body.parenting-schedule-printing .parenting-builder,
	body.parenting-schedule-printing .holiday-builder {
		overflow: visible !important;
		max-height: none !important;
		height: auto !important;
		padding: 0 !important;
		margin: 0 !important;
		width: 100% !important;
		position: static !important;
	}
	/* Bootstrap 4 ships its own print stylesheet (scss/_print.scss, compiled in
	   via variables.scss): body { min-width: 992px !important } and
	   @page { size: a3 }. The letter page box below overrides the size, but the
	   992px body is wider than the 7.5in (720px) printable width, so Chrome
	   shrinks the whole sheet to about 73% to fit. That is what made the hour
	   labels unreadable and hid the fact that two weeks fit on one page. The
	   builders' print CSS is calibrated at 1:1 against the page box, so undo
	   the min-width for the duration of our print. */
	body.parenting-schedule-printing,
	body.parenting-schedule-printing .container,
	body.parenting-schedule-printing .container-fluid {
		min-width: 0 !important;
	}
	/* Upstream keeps this in its dev harness (App.vue), which Divii does not
	   vendor — so the host page carries it. The builders' print CSS is laid out
	   against this page box.

	   CAVEAT: @page cannot be gated by a body class (same limitation noted in
	   PensionCalculator.vue), and vue-style-loader does not remove an async
	   chunk's styles when the component unmounts. So once this tab has been
	   opened, this page box applies to any browser print from the agreement
	   page for the rest of the session — including Preview. Every other tab
	   here (ParentalPlanner, PensionCalculator, SupportCalculator,
	   SeparationFactSheet, FinancialDocumentChecklist) declares @page the same
	   way, so this matches existing behaviour rather than introducing a new
	   class of problem. The server-rendered agreement PDF (DocRaptor, via
	   DownloadWrapper's named @page rules) is unaffected.
	   ponytail: accepted leak. Fix properly by injecting/removing this rule on
	   beforeprint/afterprint — do it for all of these tabs at once, not just
	   this one.
	   UPDATE: our own prints now inject PRINT_PAGE_CSS (same box, plus empty
	   margin boxes) as the last stylesheet on beforeprint and remove it on
	   afterprint, so this static copy is only the fallback for a browser that
	   does not fire beforeprint. */
	@page {
		size: letter portrait;
		margin: 0.4in 0.5in;
	}
}
</style>
